import React, { useCallback, useMemo } from 'react';
import { NetworkActiveConnectionType } from 'tizen-tv-webapis';

import { APP_VERSION } from '@smart-tv/utils/api-constants';
import { storePersistentData } from '@smart-tv/utils/persistent-data/persistent-data';
import { IS_WEBOS } from '@smart-tv/utils/is-webos';
import { SentryClient } from '@smart-tv/sentry/SentryClient';

export interface TvContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export interface TvContextValues {
  appVersion?: string;
  model?: string;
  connectionType?: string;
  platform?: string;
  platformVersion?: string;
  deviceUuid?: string;
}

export const TvContext = React.createContext({} as TvContextValues);

export const TvProvider = (props: TvContextProps) => {
  const { children } = props;

  const [appVersion, setAppVersion] = React.useState<string>();
  const [connectionType, setConnectionType] = React.useState<string>();
  const [model, setModel] = React.useState<string>();
  const [platform, setPlatform] = React.useState<string>();
  const [platformVersion, setPlatformVersion] = React.useState<string>();
  const [deviceUuid, setDeviceUuid] = React.useState<string>();

  const getConnectionTypeText = React.useCallback((connectionType: NetworkActiveConnectionType) => {
    let connectionTypeText = '';
    switch (connectionType) {
      case 0:
        connectionTypeText = 'Ingen internetforbindelse';
        break;
      case 1:
        connectionTypeText = 'Forbundet via WiFi';
        break;
      case 2:
        connectionTypeText = 'Forbundet via Mobilt netværk';
        break;
      case 3:
        connectionTypeText = 'Forbundet via Netværkskabel';
        break;
    }
    if (!navigator.onLine) return 'Ingen internetforbindelse';
    return connectionTypeText;
  }, []);

  const getTizenData = React.useCallback(() => {
    setPlatform('Tizen');

    import('tizen-tv-webapis')
      .then(({ network, productinfo }) => {
        try {
          setConnectionType(getConnectionTypeText(network.getActiveConnectionType()));
          setModel(productinfo.getRealModel());
          storePersistentData('tv_model', productinfo.getRealModel());
        } catch (error) {
          console.error('Error getting connection type or model:', error);
        }
      })
      .catch((error) => {
        console.error('Error importing tizen-tv-webapis:', error);
      });

    import('tizen-common-web')
      .then(({ systeminfo, package: appPackage }) => {
        try {
          setAppVersion(appPackage.getPackageInfo().version);
          setPlatformVersion(systeminfo.getCapabilities().platformVersion);
          setDeviceUuid(systeminfo.getCapabilities().duid);
        } catch (error) {
          console.error('Error getting app version or platform version or device UUID:', error);
        }
      })
      .catch((error) => {
        console.error('Error importing tizen-common-web:', error);
      });
  }, [getConnectionTypeText]);

  const getWebOSData = useCallback(() => {
    setPlatform('webOS');

    window.webOS?.fetchAppInfo((appInfo) => setAppVersion(appInfo?.version ?? APP_VERSION));

    window.webOS?.deviceInfo(({ modelName, sdkVersion }) => {
      setModel(modelName);
      setPlatformVersion(sdkVersion);
      storePersistentData('tv_model', modelName);
      SentryClient.setModel(modelName);
    });
  }, []);

  React.useEffect(() => {
    if (window.tizen) {
      getTizenData();
    } else if (IS_WEBOS) {
      getWebOSData();
    } else {
      setAppVersion(APP_VERSION);
      setConnectionType('Browser');
    }
  }, [getTizenData, getWebOSData]);

  const contextValue = useMemo(
    () => ({
      appVersion,
      model,
      connectionType,
      platform,
      platformVersion,
      deviceUuid
    }),
    [appVersion, model, connectionType, platform, platformVersion, deviceUuid]
  );

  return <TvContext.Provider value={contextValue}>{children}</TvContext.Provider>;
};

export default TvContext;
